import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import Icon from './AppIcon';

const ToastItem = ({ message, type, onRemove }) => {
  const [isPaused, setIsPaused] = useState(false);
  const [remainingTime, setRemainingTime] = useState(5000);
  const startTimeRef = useRef(Date.now());
  const timerRef = useRef(null);

  // Auto-remove with pause functionality
  useEffect(() => {
    const updateTimer = () => {
      if (!isPaused) {
        const elapsed = Date.now() - startTimeRef.current;
        const newRemaining = Math.max(0, 5000 - elapsed);
        setRemainingTime(newRemaining);
        
        if (newRemaining <= 0) {
          onRemove();
          return;
        }
      }
      
      timerRef.current = setTimeout(updateTimer, 50);
    };

    startTimeRef.current = Date.now() - (5000 - remainingTime);
    timerRef.current = setTimeout(updateTimer, 50);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isPaused, onRemove, remainingTime]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const config = {
    success: { icon: 'CheckCircle', color: 'text-green-500', bg: 'bg-green-500/10' },
    error: { icon: 'AlertCircle', color: 'text-red-500', bg: 'bg-red-500/10' },
    info: { icon: 'Info', color: 'text-blue-500', bg: 'bg-blue-500/10' }
  };

  const { icon, color, bg } = config[type] || config.success;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: isMobile ? -20 : 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8, filter: "blur(4px)" }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      
      // Interactive Gestures
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.7}
      onDragEnd={(e, info) => {
        if (Math.abs(info.offset.x) > 100) onRemove();
      }}

      // Pause on hover
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}

      className="pointer-events-auto relative overflow-hidden w-full md:w-[380px] bg-[var(--color-card)] border border-[var(--color-border)] shadow-2xl rounded-2xl p-4 flex items-center gap-4 cursor-grab active:cursor-grabbing group select-none"
    >
      {/* Visual Icon */}
      <div className={`flex-shrink-0 p-2.5 rounded-xl ${bg}`}>
        <Icon name={icon} size={20} className={color} />
      </div>

      {/* Message Area */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-[var(--color-foreground)] leading-snug">
          {message}
        </p>
      </div>

      {/* Mobile Handle / Desktop Close */}
      <div className="flex flex-col gap-1 opacity-20 md:hidden">
        <div className="w-1 h-1 bg-current rounded-full" />
        <div className="w-1 h-1 bg-current rounded-full" />
        <div className="w-1 h-1 bg-current rounded-full" />
      </div>
      
      <button 
        onClick={onRemove}
        className="flex-shrink-0 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors p-1.5 hover:bg-[var(--color-muted)] rounded-lg"
      >
        <Icon name="X" size={16} />
      </button>

      {/* Countdown Progress Bar */}
      <motion.div 
        initial={{ width: '100%' }}
        animate={{ width: `${(remainingTime / 5000) * 100}%` }}
        transition={{ duration: 0.05, ease: "linear" }}
        className={`absolute bottom-0 left-0 h-1 ${isPaused ? 'bg-[var(--color-muted-foreground)]' : 'bg-[var(--color-primary)]'} opacity-30`}
      />
    </motion.div>
  );
};

export default ToastItem;